/* eslint-disable @nx/enforce-module-boundaries */
import { NgClass } from '@angular/common';
import {
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { DashboardTranslateKeys } from '../../../utils/dashboard-translate-keys.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FiltersComponent } from '../../filters/filters.component';
import { SkeletonDashCardComponent } from '../../skeleton/skeleton.component';
import { ViewportObserverDirective } from '../../viewport-observer/viewport-observer.directive';
import { FilterDashOption } from '../../../utils/filters-dash.enum';
import { DashboardService } from '../../../services/dashboard.service';
import { AppGlobalService } from 'apps/job-ats/src/app/commons/services/app-global.service';
import { EMPTY, Subject, switchMap, takeUntil } from 'rxjs';
import { Company, User } from '@job-v2/ats-utils';
import {
  ColumnChart,
  DoughnutChartObject,
  OfferStatusGroup,
  OfferStatusStructure,
  StatisticsCriteria,
} from '../../../utils/dashboard.data';
import { MessageService } from 'primeng/api';
import { DoughnutChartComponent } from '../../../charts/doughnut-chart/doughnut-chart.component';
import { MultipleColumnChartComponent } from '../../../charts/multiple-column-chart/multiple-column-chart.component';
import { ColumnChartComponent } from '../../../charts/column-chart/column-chart.component';

@Component({
  imports: [
    ButtonModule,
    NgClass,
    TranslateModule,
    FiltersComponent,
    DoughnutChartComponent,
    ViewportObserverDirective,
    SkeletonDashCardComponent,
    MultipleColumnChartComponent,
    ColumnChartComponent,
  ],
  selector: 'app-job-offer-tracking',
  templateUrl: './job-offer-tracking.component.html',
  styleUrl: './job-offer-tracking.component.scss',
})
export class JobOfferTrackingComponent implements OnInit, OnDestroy {
  readonly selectedJobOwners = input<string[]>([]);
  private readonly selectedJobOwners$ = toObservable(this.selectedJobOwners);
  public isCollapsed = signal(false);
  public isInitialized = signal(false);
  public isLoadingOffersStats = signal(true);
  public isLoadingOffersAcceptance = signal(true);
  public isLoadingOverallStatusPerStructure = signal(true);
  public isLoadingAcceptanceRatePerStructure = signal(true);
  public DashboardTranslateKeys = DashboardTranslateKeys;
  private readonly translate = inject(TranslateService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly message = inject(MessageService);
  public chartOffersObjects!: DoughnutChartObject[];
  public chartOverallOfferStatusStructure!: OfferStatusStructure[];
  public chartColumnAcceptanceRateStructure!: ColumnChart[];
  public offerAcceptanceAccepted = signal(0);
  public offerAcceptanceProposed = signal(0);
  public offerAcceptancePercent = signal(0);
  private readonly destroy$ = new Subject<void>();
  private readonly hydrateOffersSubject$ = new Subject<void>();
  private offersStatusCriteria = new StatisticsCriteria();
  private offersAcceptanceCriteria = new StatisticsCriteria();
  private overallStatusPerStructureCriteria = new StatisticsCriteria();
  private acceptanceRatePerStructureCriteria = new StatisticsCriteria();
  public user!: User;
  public company!: Company;
  public firstFiltersOptions: FilterDashOption[] = [
    {
      label: this.translate.instant(DashboardTranslateKeys.OFFERS) as string,
      value: null,
    },
  ];
  private readonly service = inject(DashboardService);

  ngOnInit(): void {
    this.setupHydrateOffersStream();
    this.selectedJobOwners$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedJobOwners) => {
        this.handleSelectedJobOwnersChange(selectedJobOwners);
      });
    this.appGlobalService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (user) this.user = user;
        if (user?.company) this.company = user.company;
        this.offersStatusCriteria.companyId = user?.company?._id;
        this.offersAcceptanceCriteria.companyId = user?.company?._id;
        this.overallStatusPerStructureCriteria.companyId = user?.company?._id;
        this.acceptanceRatePerStructureCriteria.companyId = user?.company?._id;
        this.handleSelectedJobOwnersChange(this.selectedJobOwners());
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleContent() {
    this.isCollapsed.set(!this.isCollapsed());
  }

  initData() {
    if (this.isInitialized()) return;
    this.isInitialized.set(true);
    this.loadOffersStatusStats();
    this.loadOffersAcceptanceRate();
    this.loadOverallStatusPerStructure();
    this.loadAcceptanceRatePerStructure();
  }

  loadOffersStatusStats() {
    if (!this.user.company?._id) return;
    this.isLoadingOffersStats.set(true);
    this.service
      .getOffersStatusStats(this.offersStatusCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.chartOffersObjects = this.getOffersFromData(res);
          this.isLoadingOffersStats.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingOffersStats.set(false);
        },
      });
  }

  loadOffersAcceptanceRate() {
    if (!this.user.company?._id) return;
    this.isLoadingOffersAcceptance.set(true);
    this.service
      .getOfferAcceptanceRate(this.offersAcceptanceCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.offerAcceptanceAccepted.set(res.accepted || 0);
          this.offerAcceptanceProposed.set(res.proposed || 0);
          this.offerAcceptancePercent.set(this.getRatePercentage());
          this.isLoadingOffersAcceptance.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingOffersAcceptance.set(false);
        },
      });
  }

  loadOverallStatusPerStructure() {
    if (!this.user.company?._id) return;
    this.isLoadingOverallStatusPerStructure.set(true);
    this.service
      .getOverallStatusPerStructure(this.overallStatusPerStructureCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.chartOverallOfferStatusStructure = res;
          this.isLoadingOverallStatusPerStructure.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingOverallStatusPerStructure.set(false);
        },
      });
  }

  loadAcceptanceRatePerStructure() {
    if (!this.user.company?._id) return;
    this.isLoadingAcceptanceRatePerStructure.set(true);
    this.service
      .getAcceptanceRatePerStructure(this.acceptanceRatePerStructureCriteria)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.chartColumnAcceptanceRateStructure = res.map((res) => {
            return {
              structure: res.structure,
              value: res.acceptanceRate || 0,
            };
          });
          this.isLoadingAcceptanceRatePerStructure.set(false);
        },
        error: () => {
          this.displayErrorMessage();
          this.isLoadingAcceptanceRatePerStructure.set(false);
        },
      });
  }

  getRatePercentage(): number {
    if (this.offerAcceptanceProposed() === 0) return 0;
    const result =
      (this.offerAcceptanceAccepted() / this.offerAcceptanceProposed()) * 100;
    const toReturn = Math.floor(result);
    return toReturn < 100 ? toReturn : 100;
  }

  updateOffersAcceptanceJob($event: string | null) {
    this.updateCriteria(this.offersAcceptanceCriteria, 'offer', $event);
    this.loadOffersAcceptanceRate();
  }
  updateOverallStructureStatusYear($event: string | null) {
    this.updateCriteria(this.overallStatusPerStructureCriteria, 'year', $event);
    this.loadOverallStatusPerStructure();
  }
  updateOverallStructureAcceptenceYear($event: string | null) {
    this.updateCriteria(
      this.acceptanceRatePerStructureCriteria,
      'year',
      $event,
    );
    this.loadAcceptanceRatePerStructure();
  }
  getTranslatedLabel(label: string): string {
    return this.translate.instant(`menuLabel.jobs.${label}`);
  }

  private displayErrorMessage() {
    const summary = this.translate.instant(DashboardTranslateKeys.ERROR);
    const detail = this.translate.instant(
      DashboardTranslateKeys.ERROR_TRY_AGAIN,
    );
    this.message.add({ severity: 'error', summary, detail });
  }

  private updateCriteria<T extends { monthYear?: string; year?: string }>(
    criteria: T,
    key: keyof T,
    value: string | null,
  ) {
    if (value) {
      criteria[key] = value as T[keyof T];
    } else {
      delete criteria[key];
    }
  }

  updateOffersStatusMonthYear($event: string | null) {
    this.updateCriteria(this.offersStatusCriteria, 'monthYear', $event);
    this.loadOffersStatusStats();
  }

  updateOffersAcceptanceMonthYear($event: string | null) {
    this.updateCriteria(this.offersAcceptanceCriteria, 'monthYear', $event);
    this.loadOffersAcceptanceRate();
  }

  private setupHydrateOffersStream(): void {
    this.hydrateOffersSubject$
      .pipe(
        switchMap(() => {
          if (!this.company?._id) return EMPTY;
          return this.service.getLatestOffers(
            this.company._id as string,
            this.selectedJobOwners(),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res) => {
        const options = res.map((elt) => ({
          label: elt.title as string,
          value: elt.id as string,
        }));
        this.firstFiltersOptions = [...this.firstFiltersOptions, ...options];
      });
  }

  hydrateOffers(): void {
    this.hydrateOffersSubject$.next();
  }

  getOffersFromData(data: OfferStatusGroup[]) {
    const colors: Record<string, string> = {
      published: '#5884FF',
      archived: '#ffb951',
      finished: '#FB335B',
      closed: '#567890',
      notPublished: '#75CA7E',
    };
    return data.map((item) => ({
      x: this.getTranslatedLabel(item.label as string),
      y: item.percentage,
      text: `${item.percentage}%`,
      color: colors[item.label as string] || '#dadde1',
    }));
  }

  private updateSelectedJobOwners(selectedJobOwners: string[]) {
    this.offersStatusCriteria.selectedJobOwner = selectedJobOwners;
    this.offersAcceptanceCriteria.selectedJobOwner = selectedJobOwners;
    this.overallStatusPerStructureCriteria.selectedJobOwner = selectedJobOwners;
    this.acceptanceRatePerStructureCriteria.selectedJobOwner = selectedJobOwners;
  }

  private handleSelectedJobOwnersChange(selectedJobOwners: string[]) {
    if (!this.company?._id) return;
    this.updateSelectedJobOwners(selectedJobOwners);
    if (this.isInitialized()) {
      this.loadOffersStatusStats();
      this.loadOffersAcceptanceRate();
      this.loadOverallStatusPerStructure();
      this.loadAcceptanceRatePerStructure();
    }
    this.resetOfferFilters();
    this.hydrateOffers();
  }

  private resetOfferFilters() {
    this.firstFiltersOptions = this.firstFiltersOptions.filter(
      (item) => item.value === null,
    );
  }
}
